import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CasinoProvider } from '@app/shared/models/interfaces/casinoProvider';
import { GamesParams } from '@app/shared/models/casino/gameParams';
import { HacksawProvider } from '@app/shared/models/casino/hacksawGames';
import { Collection } from '@app/shared/models/casino/collections';
import {
    PragmaticActiveJackpot,
    PragmaticActiveJackpotResponse,
} from '@app/shared/models/interfaces/pragmatic-active-jackpot';
import { normalizePragmaticActiveJackpots } from '@app/shared/models/pragmatic-active-jackpots.mapper';
import { catchError } from 'rxjs/operators';

export interface GameType {
    displayName: string;
    originalName: string;
    count: number;
}

export interface Category {
    name: string;
    gameTypes: { [key: string]: GameType };
}

@Injectable({ providedIn: 'root' })
export class CasinoGameListService {
    baseUrl = environment.apiUrl;
    excludedProviders: string[] = [];
    excludedChange = new BehaviorSubject<boolean>(false);
    lang = localStorage.getItem('lang') || 'en';
    private readonly pragmaticActiveJackpotsEndpoint = '/public/pragmatic-active-jackpots';
    private readonly jackpotCasinoGamesEndpoint = '/public/jackpot-casino-games';
    private totalCasinoGamesPages = 30; // Variable to store the total number of pages of the casino games

    public hacksawGames: HacksawProvider[] = [
        {
            provider: 'Hacksaw',
            index: 7,
            route: '/casino',
            top10Games: [
                {
                    name: 'Speed Crash',
                    imagePortrait: 'assets/images/provider-casino/speed-crash.jpg',
                    imageSquare: 'assets/images/provider-casino/speed-crash.jpg',
                    gameId: '81955',
                },
                {
                    name: 'Twenty-One',
                    imagePortrait: 'assets/images/provider-casino/twenty-one.jpg',
                    imageSquare: 'assets/images/provider-casino/twenty-one.jpg',
                    gameId: '69245',
                },
                {
                    name: 'Baccarat',
                    imagePortrait: 'assets/images/provider-casino/baccarat.jpg',
                    imageSquare: 'assets/images/provider-casino/baccarat.jpg',
                    id: '73821',
                },
                {
                    name: 'Mines',
                    imagePortrait: 'assets/images/provider-casino/mines.jpg',
                    imageSquare: 'assets/images/provider-casino/mines.jpg',
                    id: '44167',
                },
                {
                    name: 'Plinko',
                    imagePortrait: 'assets/images/provider-casino/plinko.jpg',
                    imageSquare: 'assets/images/provider-casino/plinko.jpg',
                    id: '61051',
                },
                {
                    name: 'Boxes',
                    imagePortrait: 'assets/images/provider-casino/boxes.jpg',
                    imageSquare: 'assets/images/provider-casino/boxes.jpg',
                    id: '44171',
                },
                {
                    name: 'Lines',
                    imagePortrait: 'assets/images/provider-casino/lines.jpg',
                    imageSquare: 'assets/images/provider-casino/lines.jpg',
                    id: '64047',
                },
                {
                    name: 'Coins',
                    imagePortrait: 'assets/images/provider-casino/coins.jpg',
                    imageSquare: 'assets/images/provider-casino/coins.jpg',
                    id: '44175',
                },
                {
                    name: 'Wheel',
                    imagePortrait: 'assets/images/provider-casino/wheel.jpg',
                    imageSquare: 'assets/images/provider-casino/wheel.jpg',
                    id: '65157',
                },
                {
                    name: 'Colors',
                    imagePortrait: 'assets/images/provider-casino/colors.jpg',
                    imageSquare: 'assets/images/provider-casino/colors.jpg',
                    id: '69109',
                },
            ],
        },
    ];
    public collections: Collection[] = [
        {
            name: 'casino_page.joker',
            image: 'assets/images/collections/joker.svg',
            theme: 'joker',
            nameKey: 'video-bingo',
        },
        {
            name: 'casino_page.pirates',
            image: 'assets/images/collections/pirate.svg',
            theme: 'pirate',
            nameKey: 'pirates',
        },
        {
            name: 'casino_page.crash-games',
            image: 'assets/images/collections/crash-games.svg',
            theme: 'crash-games',
            nameKey: 'crash-games',
        },
        {
            name: 'casino_page.fishing-reels',
            image: 'assets/images/collections/fishing.svg',
            theme: 'fish',
            nameKey: 'fishing-reels',
        },
        {
            name: 'casino_page.megaways',
            image: 'assets/images/collections/megaways.svg',
            theme: 'megaways',
            nameKey: 'megaways',
        },
        {
            name: 'casino_page.egyptian',
            image: 'assets/images/collections/egyptian.svg',
            theme: 'egypt',
            nameKey: 'egyptian',
        },
        {
            name: 'casino_page.vikings',
            image: 'assets/images/collections/viking.svg',
            theme: 'viking',
            nameKey: 'vikings',
        },
        {
            name: 'casino_page.candy-shop',
            image: 'assets/images/collections/candy-shop.svg',
            theme: 'candy',
            nameKey: 'candy-shop',
        },
        {
            name: 'casino_page.video-bingo',
            image: 'assets/images/collections/bingo.svg',
            theme: 'bingo',
            nameKey: 'video-bingo',
        },
        {
            name: 'casino_page.scratch',
            image: 'assets/images/collections/scratch.svg',
            theme: 'scratch',
            nameKey: 'scratch',
        },
    ];

    constructor(private http: HttpClient) {}

    getGameById(gameId: any, lang: string, closeUrl?: string): Observable<any> {
        let params = new HttpParams().set('lang', lang);
        if (closeUrl) {
            params = params.set('closeUrl', closeUrl);
        }
        return this.http.get(`${this.baseUrl}/user/getGame/${gameId}`, {
            withCredentials: true,
            params: params,
        });
    }

    getRandomGame(): Observable<any> {
        return this.getGames({ page: 0, size: 1 }).pipe(
            map((response) => {
                const games = response.content;
                const gameIds = games.map((game: any) => game.id);
                const randomGameId = gameIds[Math.floor(Math.random() * gameIds.length)];
                return randomGameId;
            }),
            switchMap((gameId) => this.getGameById(gameId, this.lang)),
        );
    }

    getRandomLuckyGame(): Observable<any> {
        const page = Math.floor(Math.random() * this.totalCasinoGamesPages);
        return this.getGames({ page: page, size: 20 }).pipe(
            map((response) => {
                if (response.totalPages > this.totalCasinoGamesPages) {
                    this.totalCasinoGamesPages = response.totalPages;
                }
                const games = response.content;
                const randomIndex = Math.floor(Math.random() * games.length);
                const randomGame = games[randomIndex];
                return randomGame;
            }),
        );
    }

    getRandomGameId(): Observable<any> {
        return this.getGames({ page: 0, size: 1 }).pipe(
            map((response) => {
                const games = response.content;
                const randomGameId = games[Math.floor(Math.random() * games.length)].id;
                return randomGameId;
            }),
        );
    }

    getGames(params: GamesParams): Observable<any> {
        const httpParams = this.buildGetGamesParams(params);
        return this.http.get<any>(`${this.baseUrl}/public/getGames`, { params: httpParams });
    }

    getLiveCasinoGames(page: number, size: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/public/getLiveCasinoGames?page=${page}&size=${size}`);
    }

    getCategories(): Observable<Category[]> {
        return this.http.get<Category[]>(`${this.baseUrl}/public/getCategories`).pipe(
            map((categories: Category[]) => {
                return categories;
            }),
        );
    }

    updateFavourite(gameId: number): void {
        this.http.post(`${this.baseUrl}/user/favourite-games/${gameId}`, {}).subscribe();
    }

    deleteFavourite(gameId: number): void {
        this.http.delete(`${this.baseUrl}/user/favourite-games/${gameId}`).subscribe();
    }

    getFavouriteGames(): Observable<any> {
        return this.http.get(`${this.baseUrl}/user/favourite-games`);
    }

    getRecentlyPlayedGamed(): Observable<any> {
        return this.http.get(`${this.baseUrl}/user/recently-played-games`);
    }

    updateExcludedProviders(provider: string, action: 'add' | 'remove' | 'clear'): void {
        if (action === 'add' && !this.excludedProviders.includes(provider)) {
            this.excludedProviders.push(provider);
        } else if (action === 'remove') {
            this.excludedProviders = this.excludedProviders.filter((p) => p !== provider);
        } else if (action === 'clear') {
            this.excludedProviders = [];
        }
        this.excludedChange.next(true);
    }

    getMostPlayedCasinoGames(): Observable<any> {
        return this.http.get(`${this.baseUrl}/public/most-played-casino-games`);
    }

    getMostPlayedLiveCasinoGames(): Observable<any> {
        return this.http.get(`${this.baseUrl}/public/most-played-live-casino-games`);
    }

    getGameProviders(params?: { type: string; name: string }): Observable<CasinoProvider[]> {
        let httpParams = new HttpParams();
        if (params?.type) {
            httpParams = httpParams.set('type', params.type);
        }
        if (params?.name) {
            httpParams = httpParams.set('name', params.name);
        }
        return this.http.get<CasinoProvider[]>(`${this.baseUrl}/public/casino-games-providers`, {
            params: httpParams,
        });
    }

    getTopProviderGames(providers: string[]): Observable<any> {
        const params = new HttpParams().set('providers', providers.join(','));
        return this.http.get(`${this.baseUrl}/public/top-casino-games-providers`, { params });
    }

    getJackpotCasinoGames() {
        return this.http.get(`${this.baseUrl}${this.jackpotCasinoGamesEndpoint}`);
    }

    getPragmaticActiveJackpots(): Observable<PragmaticActiveJackpotResponse> {
        return this.getPragmaticJackpotsFrom(this.pragmaticActiveJackpotsEndpoint)
            .pipe(
                catchError(() =>
                    this.getPragmaticJackpotsFrom(this.jackpotCasinoGamesEndpoint),
                ),
            );
    }

    getTopMostPlayedCasinoGames() {
        return this.http.get(`${this.baseUrl}/public/most-played-20-casino-games`);
    }

    getCasinoBets(body: any, params: any): Observable<any> {
        return this.http.post(`${this.baseUrl}/user/games-report`, body, { params });
    }

    getLatestWinners(limit: number = 10): Observable<any> {
        const params = new HttpParams().set('limit', limit.toString());
        return this.http.get(`${this.baseUrl}/public/latest-winners`, { params });
    }

    private buildGetGamesParams(params: GamesParams): HttpParams {
        let httpParams = new HttpParams();
        if (params.page !== undefined) {
            httpParams = httpParams.set('page', params.page.toString());
        }
        if (params.size !== undefined) {
            httpParams = httpParams.set('size', params.size.toString());
        }
        if (params.type) {
            httpParams = httpParams.set('type', params.type);
        }
        if (params.name) {
            httpParams = httpParams.set('name', params.name);
        }
        if (params.category) {
            httpParams = httpParams.set('category', params.category);
        }
        if (params.categories && params.categories.length > 0) {
            httpParams = httpParams.set('categories', params.categories.join(','));
        }

        // Special case
        if (this.excludedProviders && this.excludedProviders.length > 0) {
            const excludedProvidersParam = this.excludedProviders.join(',');
            httpParams = httpParams.set('excludedSubcategories', excludedProvidersParam);
        }
        return httpParams;
    }

    private getPragmaticJackpotsFrom(
        endpoint: string,
    ): Observable<PragmaticActiveJackpotResponse> {
        return this.http
            .get<PragmaticActiveJackpotResponse | PragmaticActiveJackpot[]>(
                `${this.baseUrl}${endpoint}`,
            )
            .pipe(map((response) => normalizePragmaticActiveJackpots(response)));
    }
}
